import { useQuery } from '@apollo/client/react'

import { graphql } from '~publish/graphql'
import type { ResultOf } from '~publish/graphql'

export const GetAPIClientUsageStatistics = graphql(/* GraphQL */ `
  query getAPIClientUsageStatistics($clientId: String!) {
    apiClientUsageStatistics(input: { clientId: $clientId }) {
      ... on APIClientUsageStatistics {
        __typename
        last15Minutes {
          used
          limit
        }
        lastDay {
          used
          limit
        }
        lastMonth {
          used
          limit
        }
      }
      ... on NotFoundError {
        __typename
        message
      }
    }
  }
`)

// Keep the previous shape (no required `__typename`) so consumers that build
// this data without a typename keep type-checking after the codegen → gql.tada
// swap.
type ApiClientUsageStatistics = Omit<
  Extract<
    ResultOf<typeof GetAPIClientUsageStatistics>['apiClientUsageStatistics'],
    { __typename: 'APIClientUsageStatistics' }
  >,
  '__typename'
>

type UseAPIClientUsageStatisticsResult = {
  usageData: ApiClientUsageStatistics | null
  isLoading: boolean
  hasError: boolean
  errorMessage?: string
  refetch: () => Promise<unknown>
}

export const useAPIClientUsageStatistics = (
  clientId: string | null,
): UseAPIClientUsageStatisticsResult => {
  const { data, loading, error, refetch } = useQuery(
    GetAPIClientUsageStatistics,
    {
      variables: { clientId: clientId || '' },
      skip: !clientId,
      fetchPolicy: 'cache-and-network',
    },
  )

  const result = data?.apiClientUsageStatistics

  if (result?.__typename === 'APIClientUsageStatistics') {
    return {
      usageData: result,
      isLoading: loading,
      hasError: false,
      refetch,
    }
  }

  if (result?.__typename === 'NotFoundError') {
    return {
      usageData: null,
      isLoading: loading,
      hasError: true,
      errorMessage: result.message,
      refetch,
    }
  }

  return {
    usageData: null,
    isLoading: loading,
    hasError: !!error,
    errorMessage: error?.message,
    refetch,
  }
}
